import { BinaryFilter } from '@cubejs-client/core';

import { FilterLimitPresets } from '@/models/filter/filterDefaultLimits';
import { HIERARCHY_SEPARATOR } from '@/models/filter/utils/hierarchyPath';
import { GetOptionsQueryFunction } from '@/types/cube/query';
import { FilterConfigDynamic } from '@/types/filter/filterConfig';
import { SelectItem } from '@/types/ui/selectItem';
import { toStringOrNull } from '@/utils/cube/mapper';

import { buildSubnationalSalesPeriodFilters, SUBNATIONAL_SALES_CUBE_NAME } from './subnationalSalesOptionsQuery';

const MARKET_NAME = `${SUBNATIONAL_SALES_CUBE_NAME}.product_configuration_market_name`;
const GROUP_DIMENSIONS = [
  `${SUBNATIONAL_SALES_CUBE_NAME}.product_configuration_group_1`,
  `${SUBNATIONAL_SALES_CUBE_NAME}.product_configuration_group_2`,
  `${SUBNATIONAL_SALES_CUBE_NAME}.product_configuration_group_3`,
  `${SUBNATIONAL_SALES_CUBE_NAME}.product_configuration_group_4`,
  `${SUBNATIONAL_SALES_CUBE_NAME}.product_configuration_group_5`,
  `${SUBNATIONAL_SALES_CUBE_NAME}.product_configuration_group_6`,
];

interface MarketGroup {
  market: string;
  group: string;
  column: number;
}

const SALES_PRODUCT_GROUP_OPTIONS: GetOptionsQueryFunction = ({ dateRange, comparisonPeriod }) => ({
  dimensions: [MARKET_NAME, ...GROUP_DIMENSIONS],
  filters: buildSubnationalSalesPeriodFilters([dateRange, comparisonPeriod]),
  order: { [MARKET_NAME]: 'asc' },
  limit: FilterLimitPresets.LIMIT_1K,
});

const toMarketGroupOptions = (rowOptions: SelectItem<string>[]): SelectItem<string>[] => {
  const marketGroups = new Map<string, MarketGroup>();
  rowOptions.forEach(rowOption => {
    const marketRow: unknown = JSON.parse(rowOption.value);
    if (!Array.isArray(marketRow)) return;
    const [market, ...groups] = marketRow;
    if (typeof market !== 'string') return;
    groups.forEach((group, column) => {
      if (typeof group !== 'string') return;
      const optionValue = `${market}${HIERARCHY_SEPARATOR}${group}`;
      const knownGroup = marketGroups.get(optionValue);
      if (!knownGroup || column < knownGroup.column) marketGroups.set(optionValue, { market, group, column });
    });
  });

  return [...marketGroups]
    .sort(
      ([, first], [, second]) =>
        first.market.localeCompare(second.market) ||
        first.column - second.column ||
        first.group.localeCompare(second.group),
    )
    .map(([optionValue, { market, group }]) => ({ value: optionValue, label: group, category: market }));
};

const salesProductGroupFilter: FilterConfigDynamic = {
  label: 'global.filters.formControls.salesProductGroupLabel',
  placeholder: 'global.filters.formControls.salesProductGroupLabel',
  type: 'dropdown',
  defaultValue: '',
  clearable: true,
  collapsibleCategories: true,
  sourceCube: SUBNATIONAL_SALES_CUBE_NAME,
  getOptionsQuery: SALES_PRODUCT_GROUP_OPTIONS,
  // One row holds all six group columns, nulls included so processOptions can order the groups by column.
  mapResult: cubeRow => {
    const market = toStringOrNull(cubeRow[MARKET_NAME]);
    const groups = GROUP_DIMENSIONS.map(dimension => toStringOrNull(cubeRow[dimension]));
    if (!market || !groups.some(Boolean)) return null;
    return { value: JSON.stringify([market, ...groups]), label: market };
  },
  processOptions: toMarketGroupOptions,
  applyFilter: value => {
    if (typeof value !== 'string') return undefined;
    const [market, group, ...extraSegments] = value.split(HIERARCHY_SEPARATOR);
    if (!market || !group || extraSegments.length) return undefined;
    return {
      and: [
        { member: MARKET_NAME, operator: 'equals', values: [market] },
        { or: GROUP_DIMENSIONS.map((member): BinaryFilter => ({ member, operator: 'equals', values: [group] })) },
      ],
    };
  },
};

export default salesProductGroupFilter;
